export type AmlogicJobState = "queued" | "running" | "completed" | "failed" | "cancelled";

export interface AmlogicJobSnapshot {
  id: string;
  sessionId?: string;
  kind: string;
  state: AmlogicJobState;
  queuedAt: number;
  startedAt?: number;
  finishedAt?: number;
  error?: string;
}

export interface AmlogicJobRequest {
  jobId: string;
  sessionId: string;
  kind: string;
  run: (signal: AbortSignal) => Promise<void>;
  /** Stops protocol work that does not directly consume AbortSignal. */
  cancelRunning?: () => Promise<void> | void;
}

type JobEntry = AmlogicJobSnapshot & {
  controller: AbortController;
  run: (signal: AbortSignal) => Promise<void>;
  cancelRunning?: () => Promise<void> | void;
  resolve: () => void;
  reject: (error: unknown) => void;
};

/**
 * Global Amlogic scheduler with a per-session mutex. It keeps AbortController
 * ownership in one registry so queued and running jobs can both be cancelled
 * after the UI that created them has returned.
 */
export class AmlogicJobScheduler {
  private _max: number;
  private _running = 0;
  private readonly _pending: JobEntry[] = [];
  private readonly _jobs = new Map<string, JobEntry>();
  private readonly _runningSessions = new Set<string>();

  constructor(max = 2) {
    this._max = Math.max(1, Math.min(4, max));
  }

  get maxConcurrentJobs(): number {
    return this._max;
  }

  setMaxConcurrentJobs(value: number): void {
    this._max = Math.max(1, Math.min(4, value));
    this._drain();
  }

  get runningCount(): number {
    return this._running;
  }

  get pendingCount(): number {
    return this._pending.length;
  }

  getJob(jobId: string): AmlogicJobSnapshot | undefined {
    const job = this._jobs.get(jobId);
    return job ? this._snapshot(job) : undefined;
  }

  getJobs(): AmlogicJobSnapshot[] {
    return Array.from(this._jobs.values(), (job) => this._snapshot(job));
  }

  hasActiveJob(sessionId: string): boolean {
    return Array.from(this._jobs.values()).some(
      (job) => job.sessionId === sessionId && (job.state === "queued" || job.state === "running"),
    );
  }

  enqueueJob(request: AmlogicJobRequest): Promise<void> {
    if (this._jobs.has(request.jobId)) {
      return Promise.reject(new Error(`Amlogic job ${request.jobId} already exists.`));
    }
    return this._enqueue({
      id: request.jobId,
      sessionId: request.sessionId,
      kind: request.kind,
      controller: new AbortController(),
      run: request.run,
      cancelRunning: request.cancelRunning,
    });
  }

  /** Backward-compatible entry point for standalone jobs and existing callers. */
  enqueue(abort: AbortController, fn: (signal: AbortSignal) => Promise<void>): Promise<void> {
    return this._enqueue({
      id: crypto.randomUUID(),
      kind: "anonymous",
      controller: abort,
      run: fn,
    });
  }

  cancelJob(jobId: string): boolean {
    const job = this._jobs.get(jobId);
    if (!job || !this._isActive(job)) return false;
    job.controller.abort();
    if (job.state === "queued") {
      const index = this._pending.indexOf(job);
      if (index >= 0) this._pending.splice(index, 1);
      this._finish(job, "cancelled");
      job.resolve();
      this._drain();
    } else {
      void Promise.resolve(job.cancelRunning?.()).catch(() => undefined);
    }
    return true;
  }

  /** Cancels every queued/running job for one physical session only. */
  cancelSession(sessionId: string): number {
    const ids = Array.from(this._jobs.values())
      .filter((job) => job.sessionId === sessionId && this._isActive(job))
      .map((job) => job.id);
    for (const id of ids) this.cancelJob(id);
    return ids.length;
  }

  private _enqueue(input: {
    id: string;
    sessionId?: string;
    kind: string;
    controller: AbortController;
    run: (signal: AbortSignal) => Promise<void>;
    cancelRunning?: () => Promise<void> | void;
  }): Promise<void> {
    if (input.controller.signal.aborted) return Promise.resolve();

    return new Promise<void>((resolve, reject) => {
      const job: JobEntry = {
        id: input.id,
        sessionId: input.sessionId,
        kind: input.kind,
        state: "queued",
        queuedAt: Date.now(),
        controller: input.controller,
        run: input.run,
        cancelRunning: input.cancelRunning,
        resolve,
        reject,
      };
      this._jobs.set(job.id, job);
      this._pending.push(job);
      input.controller.signal.addEventListener("abort", () => {
        if (job.state === "queued") this.cancelJob(job.id);
      }, { once: true });
      this._drain();
    });
  }

  private _drain(): void {
    while (this._running < this._max) {
      const index = this._pending.findIndex(
        (job) => !job.sessionId || !this._runningSessions.has(job.sessionId),
      );
      if (index < 0) return;
      const [job] = this._pending.splice(index, 1);
      if (job.controller.signal.aborted) {
        this._finish(job, "cancelled");
        job.resolve();
        continue;
      }
      this._start(job);
    }
  }

  private _start(job: JobEntry): void {
    job.state = "running";
    job.startedAt = Date.now();
    this._running += 1;
    if (job.sessionId) this._runningSessions.add(job.sessionId);

    job.run(job.controller.signal).then(
      () => {
        this._finish(job, job.controller.signal.aborted ? "cancelled" : "completed");
        job.resolve();
      },
      (error: unknown) => {
        if (job.controller.signal.aborted) {
          this._finish(job, "cancelled");
          job.resolve();
        } else {
          job.error = error instanceof Error ? error.message : String(error);
          this._finish(job, "failed");
          job.reject(error);
        }
      },
    ).finally(() => {
      this._running -= 1;
      if (job.sessionId) this._runningSessions.delete(job.sessionId);
      this._drain();
    });
  }

  private _finish(job: JobEntry, state: AmlogicJobState): void {
    job.state = state;
    job.finishedAt = Date.now();
  }

  private _isActive(job: JobEntry): boolean {
    return job.state === "queued" || job.state === "running";
  }

  private _snapshot(job: JobEntry): AmlogicJobSnapshot {
    return {
      id: job.id,
      sessionId: job.sessionId,
      kind: job.kind,
      state: job.state,
      queuedAt: job.queuedAt,
      startedAt: job.startedAt,
      finishedAt: job.finishedAt,
      error: job.error,
    };
  }
}
